import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { pathFor, parsePath } from '../shell/navConfig';

const NavigationContext = createContext(null);
const MOBILE_BREAKPOINT = 768; // matches the project's existing mobile-preset threshold
const DEFAULT_PAGE = 'inbound';

// The URL is the source of truth for where you are: /order-ops/dispatch etc.
// Reloading, bookmarking or sharing a link lands on the same page + sub-tab.
function fromUrl() {
  if (typeof window === 'undefined') return null;
  return parsePath(window.location.pathname);
}

export function NavigationProvider({ children }) {
  const { auth } = useAuth();
  const initial = fromUrl();
  const [currentPage, setCurrentPage] = useState(initial?.page || DEFAULT_PAGE);
  const [navData, setNavData]         = useState(initial?.subTab ? { subTab: initial.subTab } : {});
  const appliedMobileDefault = useRef(false);

  // Admins opening ZOCS on a phone land on Lookup instead of Inbound -- once
  // per session (ref guard), so it never overrides a page they've already
  // navigated to. Only when the URL didn't name a page (a reload of
  // /order-ops stays on Order Ops).
  useEffect(() => {
    if (appliedMobileDefault.current) return;
    if (!auth || auth.access_level !== 'admin') return;
    if (fromUrl()) return;
    if (!window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`).matches) return;
    appliedMobileDefault.current = true;
    setCurrentPage('lookup');
    window.history.replaceState(null, '', pathFor('lookup'));
  }, [auth]);

  // Browser back / forward.
  useEffect(() => {
    function onPop() {
      const loc = fromUrl();
      setNavData(loc?.subTab ? { subTab: loc.subTab } : {});
      setCurrentPage(loc?.page || DEFAULT_PAGE);
    }
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  function navigate(page, data = {}) {
    setNavData(data);
    setCurrentPage(page);
    const url = pathFor(page, data.subTab);
    if (window.location.pathname !== url) window.history.pushState(null, '', url);
  }

  // Pages with their own tabs call this when the tab changes, so the URL
  // follows (replace, not push: tab clicks don't pile up in Back history).
  const setSubTab = useCallback((page, subTab) => {
    const url = pathFor(page, subTab);
    const here = window.location.pathname;
    // A deeper path under the same tab (/order-ops/courier/<booking>) belongs to that tab: keep it.
    if (here !== url && !here.startsWith(`${url}/`)) window.history.replaceState(null, '', url);
  }, []);

  return (
    <NavigationContext.Provider value={{ currentPage, navData, navigate, setCurrentPage, setSubTab }}>
      {children}
    </NavigationContext.Provider>
  );
}

export function useNavigation() {
  return useContext(NavigationContext);
}
